import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { artistName: String }
  static targets = ["miniPlayer", "fullPlayer", "fullPlayerContent", "backdrop", "playButton", "previousButton", "nextButton", "playIcon", "pauseIcon", "trackName", "artistName", "albumName", "miniTitle", "miniArtist", "videoHost", "posterLayer"]

  connect() {
    this.isExpanded = false
    this.defaultAlbumName = this.hasAlbumNameTarget ? this.albumNameTarget.textContent.trim() : ""

    document.addEventListener("keydown", this.handleKeydown)
    window.addEventListener("player:state", this.handlePlayerState)
    window.addEventListener("resize", this.setMiniPlayerHeight)

    this.setMiniPlayerHeight()
    this.syncFromPlayerController()
  }

  findYoutubeElement() {
    if (this.youtubeElement) return

    this.youtubeElement = document.getElementById("youtube-player")
    if (this.youtubeElement) {
      this.youtubeOriginalParent = this.youtubeElement.parentElement
    }
  }

  syncFromPlayerController() {
    const player = window.playerController
    if (!player) return

    this.updatePlayerReady(player.isReady)
    this.updateSkipButtons(player.canSkip())
    this.updateTrackInfo(player.activeTrack)
    this.updatePlayState(player.isPlaying)
  }

  handlePlayerState(event) {
    const { track, isPlaying, isReady, canSkip } = event.detail || {}
    this.updatePlayerReady(isReady)
    this.updateSkipButtons(canSkip)
    this.updateTrackInfo(track)
    this.updatePlayState(isPlaying)
  }

  updatePlayerReady(isReady) {
    const ready = Boolean(isReady)

    this.playButtonTargets.forEach(button => {
      button.disabled = !ready
    })
  }

  updateSkipButtons(canSkip) {
    const buttons = [...this.previousButtonTargets, ...this.nextButtonTargets]

    buttons.forEach(button => {
      button.disabled = !canSkip
    })
  }

  updateTrackInfo(track) {
    const title = track?.title || "No track playing"
    // Tracks don't carry an artist; they all belong to the page's era.
    const artist = track ? (track.artist || this.artistNameValue) : "Select a track to play"
    const album = track?.album || this.defaultAlbumName

    // Until a track is chosen, the mini player keeps its server-rendered
    // era name and artist.
    if (track) {
      this.miniTitleTarget.textContent = title
      this.miniArtistTarget.textContent = artist
    }

    if (this.hasTrackNameTarget) {
      this.trackNameTarget.textContent = title
    }

    if (this.hasArtistNameTarget) {
      this.artistNameTarget.textContent = artist
    }

    if (this.hasAlbumNameTarget && album) {
      this.albumNameTarget.textContent = album
    }
  }

  updatePlayState(isPlaying) {
    const playing = Boolean(isPlaying)

    this.playIconTargets.forEach(icon => {
      icon.classList.toggle('hidden', playing)
    })

    this.pauseIconTargets.forEach(icon => {
      icon.classList.toggle('hidden', !playing)
    })
  }

  expand(event) {
    // Don't expand if clicking the play button
    if (event.target.closest('.play-button')) {
      return
    }

    this.findYoutubeElement()
    this.isExpanded = true

    // Hide mini player
    this.miniPlayerTarget.style.opacity = "0"

    // Make overlay interactive
    this.fullPlayerTarget.classList.remove("pointer-events-none")
    this.fullPlayerTarget.classList.add("pointer-events-auto")

    // Fade in backdrop
    this.backdropTarget.classList.remove("opacity-0")
    this.backdropTarget.classList.add("opacity-100")

    // Handle Video Animation
    if (this.youtubeElement) {
      // 1. Measure Start
      const startRect = this.youtubeElement.getBoundingClientRect()

      // 2. Measure End (Hack to measure expanded state)
      const originalTransition = this.fullPlayerContentTarget.style.transition
      this.fullPlayerContentTarget.style.transition = 'none'
      this.fullPlayerContentTarget.style.height = 'calc(100dvh - env(safe-area-inset-bottom, 0px))'
      // Force layout
      this.fullPlayerContentTarget.offsetHeight
      const endRect = this.videoHostTarget.getBoundingClientRect()

      // Reset
      this.fullPlayerContentTarget.style.height = '0'
      this.fullPlayerContentTarget.offsetHeight // Force reflow
      this.fullPlayerContentTarget.style.transition = originalTransition

      // 3. Apply Fixed Position to Video at Start
      Object.assign(this.youtubeElement.style, {
        position: 'fixed',
        top: `${startRect.top}px`,
        left: `${startRect.left}px`,
        width: `${startRect.width}px`,
        height: `${startRect.height}px`,
        zIndex: '100',
        transition: 'all 300ms ease-in-out',
        display: 'block' // Ensure it's visible
      })
      this.youtubeElement.classList.remove("hidden")

      // 4. Animate to End
      requestAnimationFrame(() => {
        Object.assign(this.youtubeElement.style, {
          top: `${endRect.top}px`,
          left: `${endRect.left}px`,
          width: `${endRect.width}px`,
          height: `${endRect.height}px`
        })
      })

      this.togglePosters(true)
    }

    // Expand the height to full screen
    requestAnimationFrame(() => {
      this.fullPlayerContentTarget.style.height = `calc(100dvh - env(safe-area-inset-bottom, 0px))`
    })

    // Prevent body scroll when player is open
    document.body.style.overflow = "hidden"
    document.documentElement.style.overflow = "hidden"
  }

  collapse() {
    this.isExpanded = false

    // Fade out backdrop
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0")

    // Shrink the height back to 0 (morphs into mini player)
    this.fullPlayerContentTarget.style.height = "0"

    // Animate Video Back
    if (this.youtubeElement && this.youtubeOriginalParent) {
      const endRect = this.youtubeOriginalParent.getBoundingClientRect()

      Object.assign(this.youtubeElement.style, {
        top: `${endRect.top}px`,
        left: `${endRect.left}px`,
        width: `${endRect.width}px`,
        height: `${endRect.height}px`
      })
    }

    // Show mini player as the full player collapses
    setTimeout(() => {
      this.miniPlayerTarget.style.opacity = "1"
    }, 200) // Fade in mini player partway through collapse

    // After animation completes, make overlay non-interactive and cleanup video
    setTimeout(() => {
      this.fullPlayerTarget.classList.remove("pointer-events-auto")
      this.fullPlayerTarget.classList.add("pointer-events-none")

      if (this.youtubeElement) {
        // Reset styles to put back in flow
        this.youtubeElement.style.position = ''
        this.youtubeElement.style.top = ''
        this.youtubeElement.style.left = ''
        this.youtubeElement.style.width = ''
        this.youtubeElement.style.height = ''
        this.youtubeElement.style.zIndex = ''
        this.youtubeElement.style.transition = ''
        this.togglePosters(false)
      }
    }, 300) // Match transition duration

    // Restore body scroll
    document.body.style.overflow = ""
    document.documentElement.style.overflow = ""
  }

  togglePlay(event) {
    // Prevent event from bubbling to expand
    event.stopPropagation()

    if (window.playerController) {
      window.playerController.togglePlayPause()
    }
  }

  previous() {
    window.playerController?.previous()
  }

  next() {
    window.playerController?.next()
  }

  // Handle escape key to close
  handleKeydown(event) {
    if (event.key === "Escape" && this.isExpanded) {
      this.collapse()
    }
  }

  // Add keyboard listener
  initialize() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePlayerState = this.handlePlayerState.bind(this)
    this.setMiniPlayerHeight = this.setMiniPlayerHeight.bind(this)
  }

  // Clean up
  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    window.removeEventListener("player:state", this.handlePlayerState)
    window.removeEventListener("resize", this.setMiniPlayerHeight)
    document.body.style.overflow = ""
    document.documentElement.style.overflow = ""
  }

  setMiniPlayerHeight() {
    if (!this.hasMiniPlayerTarget) return
    const height = this.miniPlayerTarget.offsetHeight
    document.documentElement.style.setProperty("--mini-player-height", `${height}px`)
  }

  togglePosters(hide) {
    this.posterLayerTargets.forEach(el => {
      el.classList.toggle("hidden", hide)
    })
  }
}
